import { useEffect, useRef, useState } from 'react';
import PageContainer from '../components/PageContainer';
import PageTitle from '../components/PageTitle';
import SlidePhotosContainer from '../components/SlidePhotosContainer';
import Loader from '../components/Loader';
import NewError from '../components/NewError';
import CustomHr from '../components/CustomHr';
import InactiveLocationMap from '../components/mapTiler/InactiveLocationMap';
import BookForm from '../components/BookForm';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import getLink from '../utils/getLinks';
import getYoutubeEmbed from '../utils/getYoutubeEmbed';
import { useDispatch, useSelector } from 'react-redux';
import { setGallery } from '../redux/gallerySlice.js';
import useScrollPosition from '../hooks/useScrollPosition.js';
import FavouriteAndShare from '../components/FavouriteAndShare.jsx';
import { setBacklink } from '../redux/backlinkSlice.js';
import { useTranslation } from 'react-i18next';

const ActivityDetails = () => {

    // Setto il backlink su questa pagina, in modo che al ritorno in allActivities non riesegua la chiamata
    const dispatch = useDispatch();
    const location = useLocation();
    useEffect(() => {
        dispatch(setBacklink(location.pathname));
    }, [location.pathname, dispatch])

    const { t, i18n } = useTranslation();

    // Catturo url query con l'id e imposto il backlink su AllActivity
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const activityId = searchParams.get('id');
        getActivityData(activityId);
    }, [i18n.language])

    // Eseguo chiamata per i dati della singola attività e li memorizzo nello stato del componente
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [activityData, setActivityData] = useState(null);
    const getActivityData = async (id) => {
        try {
            setFetchStatus('loading');
            const token = localStorage.getItem('ticino-wild-token');
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/get-activity-user/${id}?lang=${i18n.language}`;
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };
            const options = {
                method: 'GET',
                headers,
            }
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setActivityData(result.payload);
                setFetchStatus('succeeded');
            } else {
                const error = await response.json();
                throw error;
            }
        } catch (error) {
            setError(error.message);
            setFetchStatus('failed');
        }
    }
    const [servicesIncludedLinked, setServicesIncludedLinked] = useState(null);
    useEffect(() => {
        if (activityData && activityData.servicesIncluded) {
            const linkTestString = activityData.servicesIncluded;
            const linked = linkTestString.map(element => getLink(element));
            setServicesIncludedLinked(linked)
        }
    }, [activityData])

    // Catturpo requests dell'user che se ha l'id attività nelle proprie richieste scompare il tasto prenota
    const { requests } = useSelector(state => state.user);
    const [isRequestDone, setIsRequestDone] = useState(false)
    useEffect(() => {
        if (activityData && requests.length > 0) {
            // Pesco tutte le richieste in'requests' dell'utente con activityId uguale a quello dell'attività
            const sameActivityIdRequests = requests.filter(element => element.activityId === activityData._id);
            // Se esiste un elemento e se lo status è 'picked' o 'accepted' vuol dire che la richiesta è ancora in attesa (per cui non può acquistare di nuovo questa esperienza)
            const isOnePickedOrAcceptedYet = sameActivityIdRequests.some(element => element.status === 'picked' || element.status === 'accepted');
            if (isOnePickedOrAcceptedYet) {
                setIsRequestDone(true)
            }
        }
    }, [requests, activityData])
    // Catturo userId per poter verificare che abbia i dati di acquisto impostati
    const userId = useSelector(state => state.user.id);

    // Scroll to bottom if press 'Prenota' button
    const bookRef = useRef();
    const scrollToBook = () => {
        bookRef.current.scrollIntoView({ behavior: 'smooth' })
    };

    // Fixo la descrizione con le classi a <ul> e <ol>
    const [fixedDescription, setFixedDescription] = useState(null);
    useEffect(() => {
        if (activityData && activityData.description) {
            const embedHtml = getYoutubeEmbed(activityData.description);
            setFixedDescription(embedHtml)
        }
    }, [activityData])

    // Mando l'utente alla pagina PhotosGallery al click su una foto
    const navigate = useNavigate();
    const goToGallery = (index) => {
        dispatch(setGallery({
            array: activityData.gallery,
            firstShowed: index,
            activityId: activityData._id
        }));
        navigate("/activity-gallery");
    }

    // Check dell'altezza di scroll dell'utente per fixare il button 'Prenota'
    const scrollPosition = useScrollPosition();

    // ScrollTo al click su prenota
    // Altezza totale del documento
    const [documentHeight, setDocumentHeight] = useState(null);
    const [heightToHide, setHeightToHide] = useState(null);
    useEffect(() => {
        if (activityData) {
            const scrollHeight = document.documentElement.scrollHeight;
            setDocumentHeight(scrollHeight)
        }
    }, [activityData])
    useEffect(() => {
        if (documentHeight) {
            setHeightToHide(documentHeight - 2000)
        }
    }, [documentHeight])
    const bookTarget = useRef();
    const scrollToBookTarget = () => {
        if (bookTarget.current) {
            // Decido un offset
            const topOffset = 200;
            // Ottengo la posizione in altezza dell'elemento
            const elementPosition = bookTarget.current.getBoundingClientRect().top;
            // Calcolo la posizione di scroll desiderata
            const offsetPosition = window.scrollY + elementPosition - topOffset;
            /* bookTarget.current.scrollIntoView({ behavior: 'smooth' }) */
            // Questo sopra serve per scorrere direttamente al div bookTarget
            // Invece vado all'altezza calcolata
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    }

    return (

        <PageContainer desktopItems="center">
            {/* MOBILE FIX PRENOTA BUTTON */}
            {
                activityData &&
                activityData.isPremium &&
                !activityData.isExternalPaid &&
                !isRequestDone &&
                <button className={`z-10 fixed bottom-24 md:bottom-4 self-center bg-[#225424] w-fit flex gap-2 items-center justify-center py-3 px-6 text-white rounded-xl shadow-xl xl:hidden ${scrollPosition > heightToHide && 'hidden'}`} onClick={scrollToBookTarget}>
                    <div className='pt-0.5'><i className="fi fi-rr-calendar-check"></i></div>
                    <div>{t('ActivityDetails.bookScrollButton')}</div>
                </button>
            }

            {/* COVER IMAGE */}
            {activityData && activityData.coverImageUrl && <div className='w-full h-[300px] xl:w-[1200px] xl:h-[500px] overflow-hidden mb-[-32px] relative'>
                <img src={activityData.coverImageUrl.secure_url} alt={activityData.title} className='rounded-xl h-full w-full object-cover' />
                {/* Favourite */}
                {activityData && <div className='absolute bottom-2 right-2'><FavouriteAndShare activityId={activityData._id} /></div>}
                {/* Difficulty level */}
                <div className={`
                        absolute bottom-2 left-2
                        w-fit text-white
                        py-0.5 px-1
                        flex items-center gap-2
                    `}>
                    {
                        activityData.difficultyLevel.map((level, index) => (
                            <div key={index} className={`py-0.5 px-1 rounded-md ${level === 'Facile' ? 'bg-green-500' : level === 'Medio' ? 'bg-yellow-500' : level === 'Difficile' ? 'bg-red-500' : ''}`}>{level}</div>
                        ))
                    }
                </div>
            </div>}

            {activityData && <PageTitle title={activityData.title} backLink="/" noTitle second={activityData.category.map((element, index) => <span>{index !== 0 ? ' -' : ''} {element}</span>)} />}

            {
                fetchStatus === 'failed' &&
                <NewError message={error} />
            }

            {/* Split screen */}
            <div className='flex flex-col xl:flex-row gap-8 xl:gap-4 mt-8'>
                {/* left Data */}
                {
                    activityData &&
                    <div className='flex flex-col gap-2 w-full lg:w-[800px]'>

                        {/* Title */}
                        <h1>{activityData.title}</h1>

                        {/* Summary */}
                        <h4 className='text-[#225424]'>{activityData.summary}</h4>

                        {/* INDICAZIONI STRADALI */}
                        <Link to={`https://www.google.com/maps/dir/?api=1&destination=${activityData.coordinates.lat},${activityData.coordinates.lng}`} target="_blank">
                            <button className="mt-8 w-fit border-2 border-[#225424] flex gap-2 items-center justify-center py-3 px-6 rounded-xl">
                                <div className='pt-0.5'><i className="fi fi-rr-road"></i></div>
                                <div>{t('ActivityDetails.roadDirections')}</div>
                            </button>
                        </Link>

                        {/* Dettagli */}
                        <h2 className="mt-8">{t('ActivityDetails.details')}</h2>
                        <CustomHr />

                        {/* Descrizione */}
                        <div dangerouslySetInnerHTML={{ __html: fixedDescription }}></div>

                        {/* (Tags) */}
                        <div className='flex gap-2 flex-wrap mt-8'>
                            {activityData.tags.length > 0 && activityData.tags.map((element, index) => {
                                return <div key={index} className='py-2 px-3 bg-neutral-200 rounded-xl flex items-center'>{element}</div>
                            })}
                            {
                                activityData.tags.length === 0 &&
                                <h5>{t('ActivityDetails.noTagsAlert')}</h5>
                            }
                        </div>

                        {/* PHOTO SLIDE */}
                        <br />
                        {activityData && activityData.gallery.length > 0 && <SlidePhotosContainer photoArray={activityData.gallery} goToGallery={goToGallery} />}

                        {/* In evidenza */}
                        {
                            activityData.details.length > 0 &&
                            <div>
                                <h2 className="mt-8">{t('ActivityDetails.inEvidence')}</h2>
                                <CustomHr />
                            </div>
                        }


                        <div className='w-full flex flex-col'>
                            {
                                activityData.details.length > 0 && activityData.details.map((element, index) => {
                                    return <div key={index} className='flex items-center gap-2'><i className="fi fi-sr-map-marker-check text-[#225424]"></i><div>{element}</div></div>
                                })
                            }
                        </div>

                        {/* Località */}
                        <h2 className="mt-8">{t('ActivityDetails.location')}</h2>
                        <CustomHr />

                        <div className='z-0'><InactiveLocationMap coordinates={activityData.coordinates} /></div>

                        {/* Età raccomandata */}
                        {
                            activityData.recommendedAge &&
                            <div className="flex gap-2">
                                <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                <div className='flex flex-col gap-2'>
                                    <h3>{t('ActivityDetails.recommendedAge')}</h3>
                                    <div>{t('ActivityDetails.startTo')} {activityData.recommendedAge}</div>
                                </div>
                            </div>
                        }

                        {/* Lingue disponibili */}
                        {
                            activityData.languages.length > 0 &&
                            <div className="flex gap-2">
                                <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                <div className='flex flex-col gap-2'>
                                    <h3>{t('ActivityDetails.availableLanguages')}</h3>
                                    <div className='flex flex-col gap-2'>{activityData.languages.map((element, index) => { return <li key={index}>{element}</li> })}</div>
                                </div>
                            </div>
                        }

                        {/* Altri dettagli */}
                        {
                            (
                                (activityData.program && activityData.program.length > 0) ||
                                (activityData.servicesIncluded && activityData.servicesIncluded.length > 0) ||
                                (activityData.accessibility && activityData.accessibility.length > 0) ||
                                (activityData.cancellation && activityData.cancellation.length > 0) ||
                                (activityData.equipmentRentInfo && activityData.equipmentRentInfo.length > 0) ||
                                (activityData.onsiteEquipment && activityData.onsiteEquipment.length > 0) ||
                                (activityData.precautions && activityData.precautions.length > 0) ||
                                (activityData.customOptions && activityData.customOptions.length > 0)
                            ) &&
                            <div>
                                <h2 className="mt-8">{t('ActivityDetails.otherDetails')}</h2>
                                <CustomHr />
                            </div>
                        }


                        <div className="flex flex-col gap-8">

                            {/* Programma e durata */}
                            {
                                activityData.program.length > 0 &&
                                <div className="flex gap-2">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                    <div className='flex flex-col gap-2'>
                                        <h3>{t('ActivityDetails.programAndDuration')}</h3>
                                        <div className='flex flex-col gap-2'>{activityData.program.map((element, index) => {
                                            const linkedElement = getLink(element);
                                            return <li key={index}>{linkedElement}</li>
                                        })}
                                        </div>
                                    </div>
                                </div>
                            }

                            {/* Servizi inclusi */}
                            {
                                activityData.servicesIncluded.length > 0 &&
                                <div className="flex gap-2">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                    <div className='flex flex-col gap-2'>
                                        <h3>{t('ActivityDetails.includedServices')}</h3>
                                        <div className='flex flex-col gap-2'>{
                                            activityData.servicesIncluded.map((element, index) => {
                                                const linkedElement = getLink(element);
                                                return <li key={index}>{linkedElement}</li>
                                            })
                                        }
                                        </div>
                                    </div>
                                </div>
                            }

                            {/* Accessibilità */}
                            {
                                activityData.accessibility.length > 0 &&
                                <div className="flex gap-2">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                    <div className='flex flex-col gap-2'>
                                        <h3>{t('ActivityDetails.accessibility')}</h3>
                                        <div className='flex flex-col gap-2'>{activityData.accessibility.map((element, index) => {
                                            const linkedElement = getLink(element);
                                            return <li key={index}>{linkedElement}</li>
                                        })}</div>
                                    </div>
                                </div>
                            }

                            {/* Cancellazione e rimborso */}
                            {
                                activityData.cancellation.length > 0 &&
                                <div className="flex gap-2">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                    <div className='flex flex-col gap-2'>
                                        <h3>{t('ActivityDetails.cancellationAndRefund')}</h3>
                                        <div className='flex flex-col gap-2'>{activityData.cancellation.map((element, index) => {
                                            const linkedElement = getLink(element);
                                            return <li key={index}>{linkedElement}</li>
                                        })}</div>
                                    </div>
                                </div>
                            }

                            {/* Noleggio attrezzatura */}
                            {
                                activityData.equipmentRentInfo.length > 0 &&
                                <div className="flex gap-2">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                    <div className='flex flex-col gap-2'>
                                        <h3>{t('ActivityDetails.equipmentRental')}</h3>
                                        <div className='flex flex-col gap-2'>{activityData.equipmentRentInfo.map((element, index) => {
                                            const linkedElement = getLink(element);
                                            return <li key={index}>{linkedElement}</li>
                                        })}</div>
                                    </div>
                                </div>
                            }

                            {/* Attrezzature presenti in loco */}
                            {
                                activityData.onsiteEquipment.length > 0 &&
                                <div className="flex gap-2">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                    <div className='flex flex-col gap-2'>
                                        <h3>{t('ActivityDetails.equipmentOnSite')}</h3>
                                        <div className='flex flex-col gap-2'>{activityData.onsiteEquipment.map((element, index) => {
                                            const linkedElement = getLink(element);
                                            return <li key={index}>{linkedElement}</li>
                                        })}</div>
                                    </div>
                                </div>
                            }

                            {/* Avvertenze */}
                            {
                                activityData.precautions.length > 0 &&
                                <div className="flex gap-2 w-full max-w-full overflow-hidden">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2 flex-shrink-0"></i>
                                    <div className='flex flex-col gap-2 w-full overflow-hidden'>
                                        <h3>{t('ActivityDetails.warnings')}</h3>
                                        <div className='flex flex-col gap-2 break-words w-full overflow-wrap-anywhere'>
                                            {activityData.precautions.map((element, index) => {
                                                const linkedElement = getLink(element);
                                                return (
                                                    <li key={index} className="w-full overflow-hidden text-wrap">
                                                        {linkedElement}
                                                    </li>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            }

                            {/* Dettagli tecnici */}
                            {
                                activityData.technical.length > 0 &&
                                <div className="flex gap-2">
                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                    <div className='flex flex-col gap-2'>
                                        <h3>{t('ActivityDetails.technical')}</h3>
                                        <div className='flex flex-col gap-2'>{activityData.technical.map((element, index) => {
                                            const linkedElement = getLink(element);
                                            return <li key={index}>{linkedElement}</li>
                                        })}</div>
                                    </div>
                                </div>
                            }

                            {/* OPZIONI PERSONALIZZATE */}
                            {
                                activityData.customOptions.length > 0 &&
                                <div className='flex flex-col gap-8'>
                                    {
                                        activityData.customOptions.map((element, index) => (
                                            <div key={index} className="flex flex-col gap-2">
                                                <div className="flex gap-2">
                                                    <i className="fi fi-sr-arrow-circle-right text-[#225424] pt-2"></i>
                                                    <h3>{element.title}:</h3>
                                                </div>

                                                <div className='flex flex-col gap-2 ml-6'>{element.values.map((element, index) => {
                                                    const linkedElement = getLink(element);
                                                    return <li key={index}>{linkedElement}</li>
                                                })}</div>
                                            </div>

                                        ))
                                    }
                                </div>
                            }

                            {/* Condizioni meteorologiche */}
                            {
                                activityData.isByWeather &&
                                <div className="flex gap-2 border border-[#225424] p-2 rounded-xl">
                                    <i className="fi fi-rr-exclamation text-[#225424] pt-[3px]"></i>
                                    <div>{t('ActivityDetails.notInCaseOfRain')}</div>
                                </div>
                            }
                            {/* Attività esclusiva */}
                            {
                                activityData.isExclusive &&
                                <div className="flex gap-2 border border-[#225424] p-2 rounded-xl">
                                    <i className="fi fi-rr-exclamation text-[#225424] pt-[3px]"></i>
                                    <div>{t('ActivityDetails.isExclusive')}</div>
                                </div>
                            }
                        </div>
                    </div>
                }

                {/* Right screen */}
                {
                    activityData &&
                    <div ref={bookTarget}>
                        <BookForm
                            userId={userId} activityId={activityData._id}
                            priceArray={activityData.price}
                            priceNote={activityData.priceNote}
                            isPremium={activityData.isPremium}
                            isRequestDone={isRequestDone}
                            isExternalPaid={activityData.isExternalPaid}
                        />
                    </div>
                }
            </div>
        </PageContainer>

    )
}

export default ActivityDetails;